/**
 * RoomCluster.ts
 * Multi-cell room grouping for the dungeon editor.
 *
 * Game masters think in rooms, not individual cells. A boss arena
 * might be 4 octagons, a treasury 2-3. This tool lets users
 * Ctrl+click 2-6 adjacent cells, name them as a room, and auto-opens
 * all shared walls with type/theme cascading.
 */

import type { GridCell, GridPos, CellType, WallState } from '../types/OctileTypes.ts'
import { SAFE_ZONE_TYPES } from '../types/OctileTypes.ts'
import { getCell, setWall } from '../grid/OctileGrid.ts'
import { getNeighbors, getCellType, getOctWallToward, getConnWallToward, gridKey } from '../grid/GridAddressing.ts'
import { getReciprocal } from './WallTool.ts'

// ─── Types ──────────────────────────────────────────────────

export type RoomType = 'custom' | 'boss_arena' | 'treasury' | 'shrine_complex'

export interface RoomClusterDef {
    id: string
    name: string
    cells: GridPos[]
    type: RoomType
    difficulty?: number
    createdAt: string
}

export interface WallChange {
    row: number
    col: number
    wallIndex: number
    state: WallState
}

// ─── RoomCluster ────────────────────────────────────────────

export class RoomCluster {
    private grid: GridCell[][]
    private gridWidth: number
    private gridHeight: number
    private rooms: Map<string, RoomClusterDef> = new Map()

    constructor(grid: GridCell[][], gridWidth: number, gridHeight: number) {
        this.grid = grid
        this.gridWidth = gridWidth
        this.gridHeight = gridHeight
    }

    setGrid(grid: GridCell[][], gridWidth: number, gridHeight: number): void {
        this.grid = grid
        this.gridWidth = gridWidth
        this.gridHeight = gridHeight
        this.rooms.clear()
    }

    // ─── Room CRUD ──────────────────────────────────────

    createRoom(cells: GridPos[], name: string, type: RoomType = 'custom'): {
        room: RoomClusterDef
        wallChanges: WallChange[]
    } | null {
        if (cells.length < 2 || cells.length > 6) return null

        // All cells must be active
        for (const pos of cells) {
            const cell = getCell(this.grid, pos.row, pos.col)
            if (!cell?.active) return null
        }

        // No cell already in another room
        for (const pos of cells) {
            if (this.getRoomForCell(pos.row, pos.col)) return null
        }

        // Must be contiguous
        if (!this.isContiguous(cells)) return null

        const id = crypto.randomUUID()

        // Open shared walls
        const wallChanges = this.openSharedWalls(cells)

        // Assign cluster ID
        for (const pos of cells) {
            const cell = getCell(this.grid, pos.row, pos.col)
            if (!cell) continue
            if (cell.kind === 'octagon') cell.partOfRoom = id
            if (cell.kind === 'connector') cell.isPartOfRoom = id
        }

        const room: RoomClusterDef = {
            id,
            name,
            cells: [...cells],
            type,
            createdAt: new Date().toISOString(),
        }

        // Apply type side effects
        this.applyTypeSideEffects(room)

        this.rooms.set(id, room)
        return { room, wallChanges }
    }

    dissolveRoom(roomId: string): void {
        const room = this.rooms.get(roomId)
        if (!room) return

        // Clear cluster IDs (walls stay open)
        for (const pos of room.cells) {
            const cell = getCell(this.grid, pos.row, pos.col)
            if (!cell) continue
            if (cell.kind === 'octagon') cell.partOfRoom = null
            if (cell.kind === 'connector') cell.isPartOfRoom = null
        }

        this.rooms.delete(roomId)
    }

    addCell(roomId: string, cell: GridPos): { wallChanges: WallChange[] } | null {
        const room = this.rooms.get(roomId)
        if (!room) return null
        if (room.cells.length >= 6) return null

        // Must be active
        const gridCell = getCell(this.grid, cell.row, cell.col)
        if (!gridCell?.active) return null

        // Must not be in another room
        if (this.getRoomForCell(cell.row, cell.col)) return null

        // Must be adjacent to at least one existing member
        const isAdj = room.cells.some(existing => this.areAdjacent(existing, cell))
        if (!isAdj) return null

        // Open walls to existing members
        const wallChanges = this.openSharedWalls([...room.cells, cell])
        // Only return new changes (filter to those involving the new cell)
        const newChanges = wallChanges.filter(wc =>
            (wc.row === cell.row && wc.col === cell.col) ||
            room.cells.some(rc => {
                const recip = getReciprocal(cell.row, cell.col, 0)
                return recip && recip.row === wc.row && recip.col === wc.col
            })
        )

        // Assign cluster ID
        if (gridCell.kind === 'octagon') gridCell.partOfRoom = roomId
        if (gridCell.kind === 'connector') gridCell.isPartOfRoom = roomId

        room.cells.push({ ...cell })
        return { wallChanges: newChanges.length > 0 ? newChanges : wallChanges }
    }

    removeCell(roomId: string, cell: GridPos): boolean {
        const room = this.rooms.get(roomId)
        if (!room) return false

        const idx = room.cells.findIndex(c => c.row === cell.row && c.col === cell.col)
        if (idx === -1) return false

        // Room needs at least 2 cells
        if (room.cells.length <= 2) return false

        // Check contiguity after removal
        const remaining = room.cells.filter((_, i) => i !== idx)
        if (!this.isContiguous(remaining)) return false

        // Clear cluster ID
        const gridCell = getCell(this.grid, cell.row, cell.col)
        if (gridCell?.kind === 'octagon') gridCell.partOfRoom = null
        if (gridCell?.kind === 'connector') gridCell.isPartOfRoom = null

        room.cells.splice(idx, 1)
        return true
    }

    renameRoom(roomId: string, name: string): void {
        const room = this.rooms.get(roomId)
        if (room) room.name = name
    }

    // ─── Queries ────────────────────────────────────────

    getRooms(): RoomClusterDef[] {
        return [...this.rooms.values()]
    }

    getRoom(id: string): RoomClusterDef | null {
        return this.rooms.get(id) ?? null
    }

    getRoomForCell(row: number, col: number): RoomClusterDef | null {
        for (const room of this.rooms.values()) {
            if (room.cells.some(c => c.row === row && c.col === col)) {
                return room
            }
        }
        return null
    }

    // ─── Validation ─────────────────────────────────────

    isContiguous(cells: GridPos[]): boolean {
        if (cells.length <= 1) return true

        const cellSet = new Set(cells.map(c => gridKey(c.row, c.col)))
        const visited = new Set<string>()
        const queue: GridPos[] = [cells[0]]
        visited.add(gridKey(cells[0].row, cells[0].col))

        while (queue.length > 0) {
            const current = queue.shift()!
            const neighbors = getNeighbors(current.row, current.col, this.gridWidth, this.gridHeight)

            for (const n of neighbors) {
                const key = gridKey(n.row, n.col)
                if (!visited.has(key) && cellSet.has(key)) {
                    visited.add(key)
                    queue.push(n)
                }
            }
        }

        return visited.size === cells.length
    }

    // ─── Wall Management ────────────────────────────────

    private openSharedWalls(cells: GridPos[]): WallChange[] {
        const changes: WallChange[] = []
        const seen = new Set<string>()

        for (let i = 0; i < cells.length; i++) {
            for (let j = i + 1; j < cells.length; j++) {
                const a = cells[i]
                const b = cells[j]
                if (!this.areAdjacent(a, b)) continue

                const pairKey = `${gridKey(a.row, a.col)}-${gridKey(b.row, b.col)}`
                if (seen.has(pairKey)) continue
                seen.add(pairKey)

                // Find wall index from A toward B
                const kindA = getCellType(a.row, a.col)
                let wallA = -1
                if (kindA === 'octagon') {
                    wallA = getOctWallToward(a.row, a.col, b.row, b.col)
                } else if (kindA === 'connector') {
                    wallA = getConnWallToward(a.row, a.col, b.row, b.col)
                }

                if (wallA >= 0) {
                    setWall(this.grid, a.row, a.col, wallA, 'OPEN')
                    changes.push({ row: a.row, col: a.col, wallIndex: wallA, state: 'OPEN' })

                    // Reciprocal
                    const recip = getReciprocal(a.row, a.col, wallA)
                    if (recip) {
                        const nCell = getCell(this.grid, recip.row, recip.col)
                        if (nCell?.active) {
                            setWall(this.grid, recip.row, recip.col, recip.wallIndex, 'OPEN')
                            changes.push({ row: recip.row, col: recip.col, wallIndex: recip.wallIndex, state: 'OPEN' })
                        }
                    }
                }
            }
        }

        return changes
    }

    private areAdjacent(a: GridPos, b: GridPos): boolean {
        const neighbors = getNeighbors(a.row, a.col, this.gridWidth, this.gridHeight)
        return neighbors.some(n => n.row === b.row && n.col === b.col)
    }

    // ─── Type Side Effects ──────────────────────────────

    private applyTypeSideEffects(room: RoomClusterDef): void {
        for (const pos of room.cells) {
            const cell = getCell(this.grid, pos.row, pos.col)
            if (!cell || cell.kind !== 'octagon') continue

            switch (room.type) {
                case 'boss_arena':
                    cell.type = 'boss_throne'
                    cell.difficulty = 10
                    room.difficulty = 10
                    break
                case 'treasury':
                    cell.type = 'vault'
                    break
                case 'shrine_complex':
                    cell.type = 'shrine'
                    cell.spawns = []
                    cell.traps = []
                    break
            }
        }
    }
}
